import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { Control, useController, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as zod from 'zod'
import { FiDollarSign } from 'react-icons/fi'
import {
  Field,
  Flex,
  Input,
  Message,
  Modal,
  Select,
  toast,
} from '@workwhile/ui'
import { useCreateLeadShiftsMutation, useLeadShifts } from 'queries/shift'
import { QueryData } from 'queries/types'
import {
  DEFAULT_SHIFT_LEAD_EXTRA_TIME_DURATION,
  SHIFT_LEAD_EXTRA_TIME_DURATION,
} from 'lib/time'

// Mirrors the cap the backend enforces on leads per shift
const MAX_SHIFT_LEADS = 10

const extraTimeSchema = zod.object({
  label: zod.string(),
  value: zod.number(),
})

const createSchema = (maxShiftLeads: number) =>
  zod.object({
    shiftLeadsNeeded: zod.coerce
      .number({ invalid_type_error: 'Please enter a number' })
      .int('Please enter a whole number')
      .min(1, 'Please request at least one shift lead')
      .max(
        maxShiftLeads,
        `You can request at most ${maxShiftLeads} shift leads for this shift`
      ),
    earlyStart: extraTimeSchema,
    laterEnd: extraTimeSchema,
    pay: zod.coerce
      .number({ invalid_type_error: 'Please enter a number' })
      .positive('Please enter a non-zero pay amount'),
  })

type FormValues = zod.input<ReturnType<typeof createSchema>>
type FormOutput = zod.output<ReturnType<typeof createSchema>>

interface ExtraTimeFieldProps {
  name: 'earlyStart' | 'laterEnd'
  control: Control<FormValues, unknown, FormOutput>
  label: string
  ariaLabel: string
  error?: string
  isFirst?: boolean
}

const ExtraTimeField = ({
  name,
  control,
  label,
  ariaLabel,
  error,
  isFirst,
}: ExtraTimeFieldProps) => {
  const { field } = useController({ name, control })

  return (
    <Field
      width={[1, 1 / 2]}
      mr={isFirst ? [0, 2] : undefined}
      ml={isFirst ? undefined : [0, 2]}
      label={label}
      error={error}
    >
      <Select
        isMulti={false}
        aria-label={ariaLabel}
        options={SHIFT_LEAD_EXTRA_TIME_DURATION}
        value={field.value}
        onChange={field.onChange}
      />
    </Field>
  )
}

interface Props {
  shiftId: number
  workersNeeded: number
  paymentType: QueryData<typeof useLeadShifts>['paymentType']
  trigger: ReactNode
}

export const CreateLeadShiftsModal = ({
  shiftId,
  workersNeeded,
  paymentType,
  trigger,
}: Props) => {
  const isHourly = paymentType === 'hourly'
  const maxShiftLeads = Math.min(MAX_SHIFT_LEADS, workersNeeded)
  const closeModalRef = useRef<() => void>(null)
  const [error, setError] = useState('')

  const schema = useMemo(() => createSchema(maxShiftLeads), [maxShiftLeads])

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues, unknown, FormOutput>({
    resolver: zodResolver(schema),
    defaultValues: {
      earlyStart: DEFAULT_SHIFT_LEAD_EXTRA_TIME_DURATION,
      laterEnd: DEFAULT_SHIFT_LEAD_EXTRA_TIME_DURATION,
    },
  })

  const handleSuccess = useCallback(() => {
    setError('')
    closeModalRef.current?.()
    toast.success('Lead shifts created')
  }, [])

  const handleError = useCallback((err: Error) => {
    setError(err.message)
  }, [])

  const { mutate: createLeadShifts, isPending } = useCreateLeadShiftsMutation(
    shiftId,
    { onSuccess: handleSuccess, onError: handleError }
  )

  const handleOk = useCallback(
    ({ closeModal }: { closeModal: () => void }) =>
      handleSubmit((data) => {
        closeModalRef.current = closeModal
        createLeadShifts({
          shiftLeadsNeeded: data.shiftLeadsNeeded,
          earlyStartInMinutes: data.earlyStart.value,
          laterEndInMinutes: data.laterEnd.value,
          ...(isHourly ? { payRate: data.pay } : { payLumpSum: data.pay }),
        })
      })(),
    [handleSubmit, createLeadShifts, isHourly]
  )

  return (
    <Modal
      trigger={trigger}
      loading={isPending}
      title="Create lead shifts"
      okText="Create lead shifts"
      onOk={handleOk}
    >
      <Field
        label="Number of shift leads"
        error={errors.shiftLeadsNeeded?.message?.toString()}
      >
        <Input
          {...register('shiftLeadsNeeded')}
          aria-label="Number of shift leads"
          placeholder={`1 - ${maxShiftLeads}`}
        />
      </Field>
      <Flex flexDirection={['column', 'row']}>
        <ExtraTimeField
          isFirst
          name="earlyStart"
          control={control}
          label="Early clock in before shift starts"
          ariaLabel="Select Early Start Time"
          error={errors.earlyStart?.message?.toString()}
        />
        <ExtraTimeField
          name="laterEnd"
          control={control}
          label="Late clock out after shift ends"
          ariaLabel="Select Late End Time"
          error={errors.laterEnd?.message?.toString()}
        />
      </Flex>
      <Field
        label={
          isHourly
            ? 'Enter the hourly rate per shift lead for this shift'
            : 'Enter the amount (per each shift lead) for the entire shift'
        }
        error={errors.pay?.message?.toString()}
      >
        <Input
          {...register('pay')}
          aria-label="Enter Shift Lead Pay Amount"
          type="number"
          step="0.01"
          max={100000}
          placeholder="Enter an amount"
          icon={<FiDollarSign />}
        />
      </Field>
      {error ? <Message variant="error">{error}</Message> : null}
    </Modal>
  )
}
